import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { cidadeService } from '@/services/api';
import { Cidade } from '@/types';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';

const cidadeSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  uf: z.string().min(2, 'UF deve ter 2 caracteres').max(2, 'UF deve ter 2 caracteres'),
});

type CidadeFormData = Omit<Cidade, 'id'>;

export const NovaCidadePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!id;
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateCidades } = useQueryInvalidation();
  const [loadError, setLoadError] = useState<string | null>(null);

  // Estados para o select com busca de UF
  const [ufQuery, setUfQuery] = useState<string>("");
  const [openUf, setOpenUf] = useState<boolean>(false);
  const ufRef = useRef<HTMLDivElement | null>(null);

  // Lista de todos os estados brasileiros
  const estadosBrasileiros = [
    { sigla: 'AC', nome: 'Acre' },
    { sigla: 'AL', nome: 'Alagoas' },
    { sigla: 'AP', nome: 'Amapá' },
    { sigla: 'AM', nome: 'Amazonas' },
    { sigla: 'BA', nome: 'Bahia' },
    { sigla: 'CE', nome: 'Ceará' },
    { sigla: 'DF', nome: 'Distrito Federal' },
    { sigla: 'ES', nome: 'Espírito Santo' },
    { sigla: 'GO', nome: 'Goiás' },
    { sigla: 'MA', nome: 'Maranhão' },
    { sigla: 'MT', nome: 'Mato Grosso' },
    { sigla: 'MS', nome: 'Mato Grosso do Sul' },
    { sigla: 'MG', nome: 'Minas Gerais' },
    { sigla: 'PA', nome: 'Pará' },
    { sigla: 'PB', nome: 'Paraíba' },
    { sigla: 'PR', nome: 'Paraná' },
    { sigla: 'PE', nome: 'Pernambuco' },
    { sigla: 'PI', nome: 'Piauí' },
    { sigla: 'RJ', nome: 'Rio de Janeiro' },
    { sigla: 'RN', nome: 'Rio Grande do Norte' },
    { sigla: 'RS', nome: 'Rio Grande do Sul' },
    { sigla: 'RO', nome: 'Rondônia' },
    { sigla: 'RR', nome: 'Roraima' },
    { sigla: 'SC', nome: 'Santa Catarina' },
    { sigla: 'SP', nome: 'São Paulo' },
    { sigla: 'SE', nome: 'Sergipe' },
    { sigla: 'TO', nome: 'Tocantins' }
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<CidadeFormData>({
    resolver: zodResolver(cidadeSchema),
  });

  // Filtro para o select de UF
  const filteredEstados = estadosBrasileiros.filter(estado =>
    !ufQuery || 
    estado.sigla.toLowerCase().includes(ufQuery.toLowerCase()) || 
    estado.nome.toLowerCase().includes(ufQuery.toLowerCase())
  );

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: any) {
      const target = event.target;
      if (ufRef.current && target && !ufRef.current.contains(target)) {
        setOpenUf(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Buscar dados da cidade (se editando)
  const { data: cidadeData, isLoading: loadingCidade, error: cidadeError } = useQuery<Cidade>({
    queryKey: ['cidade', id],
    queryFn: () => cidadeService.getById(Number(id)),
    enabled: isEditing,
  });

  useEffect(() => {
    if (cidadeError) {
      const message = handleError(cidadeError as any, 'Erro ao carregar cidade');
      setLoadError(message);
    }
  }, [cidadeError, handleError]);

  useEffect(() => {
    if (cidadeData) {
      setLoadError(null);
    }
  }, [cidadeData]);

  const createCidadeMutation = useMutation({
    mutationFn: cidadeService.create,
    onSuccess: () => {
      invalidateCidades();
      handleSuccess('Cidade criada com sucesso!');
      navigate('/cidades');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao criar cidade');
    },
  });

  const updateCidadeMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CidadeFormData }) =>
      cidadeService.update(id, data),
    onSuccess: () => {
      invalidateCidades(id);
      handleSuccess('Cidade atualizada com sucesso!');
      navigate('/cidades');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao atualizar cidade');
    },
  });

  // Preencher formulário com dados existentes (se editando)
  useEffect(() => {
    if (cidadeData) {
      setValue('nome', cidadeData.nome);
      setValue('uf', cidadeData.uf);

      // Definir valor inicial para o select de UF com busca
      const estado = estadosBrasileiros.find(e => e.sigla === cidadeData.uf);
      if (estado) {
        setUfQuery(`${estado.sigla} - ${estado.nome}`);
      }
    }
  }, [cidadeData, setValue]);

  const onSubmit = async (data: CidadeFormData) => {
    try {
      setIsSubmitting(true);
      if (isEditing) {
        await updateCidadeMutation.mutateAsync({ id: Number(id), data });
      } else {
        await createCidadeMutation.mutateAsync(data);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state para edição
  if (isEditing && loadError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Editar Cidade</h1>
          <p className="mt-1 text-sm text-gray-500">Não foi possível carregar as informações da cidade.</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Erro ao carregar dados</CardTitle>
            <CardDescription>{loadError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/cidades')}>Voltar para lista</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isEditing && loadingCidade) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando dados da cidade...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Cidade' : 'Nova Cidade'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing 
            ? 'Edite as informações da cidade' 
            : 'Cadastre uma nova cidade no sistema'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Dados da Cidade</CardTitle>
            <CardDescription>
              Informações básicas da cidade
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="nome">Nome da Cidade *</Label>
                <Input
                  id="nome"
                  placeholder="Nome da cidade"
                  {...register('nome')}
                  className={errors.nome ? 'border-error-500' : ''}
                />
                {errors.nome && (
                  <p className="mt-1 text-sm text-error-600">{errors.nome.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="uf">UF *</Label>
                <div className="relative" ref={ufRef}>
                  <input
                    type="text"
                    placeholder="Selecione o estado"
                    className={`w-full border pl-3 pr-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.uf ? 'border-error-500' : 'border-gray-300'
                    }`}
                    value={ufQuery}
                    onChange={(e) => { 
                      setUfQuery(e.target.value); 
                      setOpenUf(true); 
                    }}
                    onFocus={() => setOpenUf(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpenUf(false); }}
                  />
                  {ufQuery && (
                    <button
                      type="button"
                      aria-label="Limpar UF"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => { 
                        setUfQuery(""); 
                        setValue('uf', '');
                      }}
                    >
                      ×
                    </button>
                  )}
                  {openUf && (
                    <div
                      className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border bg-white shadow"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="px-2 py-1 text-xs text-gray-500">Estados</div>
                      {filteredEstados.map(estado => (
                        <button
                          key={estado.sigla}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          onClick={() => { 
                            setValue('uf', estado.sigla);
                            setUfQuery(`${estado.sigla} - ${estado.nome}`);
                            setOpenUf(false); 
                          }}
                        >
                          {estado.sigla} - {estado.nome}
                        </button>
                      ))}
                      {filteredEstados.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Nenhum estado encontrado</div>
                      )}
                    </div>
                  )}
                </div>
                {errors.uf && (
                  <p className="mt-1 text-sm text-error-600">{errors.uf.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/cidades')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || loadingCidade}
          >
            {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar Cidade' : 'Criar Cidade')}
          </Button>
        </div>
      </form>
    </div>
  );
};
